import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, FileText, IndianRupee, Mail, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { inr } from "@/pages/tech/ui";
import { techSend, patchCache, cachedGet } from "@/pages/tech/offline";

const METHODS = [["cash", "Cash"], ["upi", "UPI"], ["card", "Card"], ["online", "Online"]];

const SendInvoice = ({ job }) => {
  const [email, setEmail] = useState(job.email || "");
  const [busy, setBusy] = useState("");
  const { data: cfg } = useQuery({ queryKey: ["tech", "config"], queryFn: () => cachedGet("/tech/config"), staleTime: 300000 });
  const send = async (channel) => {
    setBusy(channel);
    try {
      const { data } = await api.post(`/tech/jobs/${job.id}/send-invoice`, { channel, email: channel === "email" ? email : null });
      toast.success(`Invoice sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(e.response ? errMsg(e) : "You're offline — sending needs internet");
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="space-y-2 border-t border-border pt-3" data-testid="tech-send-invoice">
      <p className="text-sm font-semibold">Send invoice to customer</p>
      <div className="flex gap-2">
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Customer email" data-testid="tech-send-email-input" />
        <Button variant="outline" disabled={!!busy || !email} onClick={() => send("email")} data-testid="tech-send-email">{busy === "email" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}<span className="ml-2">Email</span></Button>
      </div>
      <Button variant="outline" className="w-full" disabled={!!busy || !cfg?.whatsapp} onClick={() => send("whatsapp")} data-testid="tech-send-whatsapp">
        <MessageCircle className="h-4 w-4 mr-2" />{cfg?.whatsapp ? `Send on WhatsApp to ${job.phone}` : "WhatsApp sending not set up yet"}
      </Button>
    </div>
  );
};

export const JobBilling = ({ job }) => {
  const qc = useQueryClient();
  const inv = job.invoice;
  const [method, setMethod] = useState("cash");
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState("");
  const call = async (key, fn, ok) => {
    setBusy(key);
    try {
      const res = await fn();
      if (res?.queued) {
        const upd = (j) => ({ ...j, invoice: { ...j.invoice, status: "paid", paid: j.invoice.total }, payment_status: "paid" });
        qc.setQueryData(["tech", "job", job.id], (j) => j && upd(j));
        patchCache(`/tech/jobs/${job.id}`, upd);
        toast.message("Payment saved offline — will sync when internet is back");
        return;
      }
      toast.success(ok);
      qc.invalidateQueries({ queryKey: ["tech"] });
    } catch (e) {
      toast.error(e.response ? errMsg(e) : "You're offline — this needs internet");
    } finally {
      setBusy("");
    }
  };
  const paid = inv && inv.status === "paid";
  return (
    <div className="card-soft p-4 space-y-4" data-testid="tech-billing">
      <div className="flex items-center justify-between"><p className="font-semibold">Invoice & payment</p>{inv && <span className={`text-xs font-semibold capitalize ${paid ? "text-primary" : "text-amber-600"}`} data-testid="tech-invoice-status">{inv.status}</span>}</div>
      {inv ? (
        <div className="text-sm space-y-1.5" data-testid="tech-invoice">
          <p className="text-xs text-muted-foreground">{inv.invoice_no}</p>
          {inv.items.map((i, k) => <div key={k} className="flex justify-between"><span>{i.description}</span><span>{inr(i.amount)}</span></div>)}
          <div className="flex justify-between font-bold border-t border-border pt-1.5"><span>Total</span><span data-testid="tech-invoice-total">{inr(inv.total)}</span></div>
        </div>
      ) : <p className="text-sm text-muted-foreground">Save the repair report first, then generate the invoice from it.</p>}
      {!paid && <Button variant="outline" className="rounded-full" disabled={!!busy} onClick={() => call("inv", () => api.post(`/tech/jobs/${job.id}/invoice`), "Invoice generated")} data-testid="tech-generate-invoice">{busy === "inv" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}{inv ? "Regenerate invoice" : "Generate invoice"}</Button>}
      {inv && !paid && (
        <div className="space-y-2 border-t border-border pt-3">
          <p className="text-sm font-semibold">Payment received by</p>
          <div className="grid grid-cols-4 gap-2">{METHODS.map(([k, l]) => <button key={k} type="button" onClick={() => setMethod(k)} className={`rounded-xl border py-2 text-sm font-semibold ${method === k ? "bg-primary text-primary-foreground border-primary" : "border-border"}`} data-testid={`tech-pay-${k}`}>{l}</button>)}</div>
          {method !== "cash" && <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Transaction / UPI reference (optional)" data-testid="tech-pay-reference" />}
          <Button className="rounded-full w-full h-11" disabled={!!busy} onClick={() => window.confirm(`Confirm ${inr(inv.total - (inv.paid || 0))} received by ${method.toUpperCase()}?`) && call("pay", () => techSend("post", `/tech/jobs/${job.id}/payment`, { method, reference: ref }, `payment · ${job.booking_no}`), "Payment recorded")} data-testid="tech-mark-paid">
            {busy === "pay" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <IndianRupee className="h-4 w-4 mr-2" />}Mark {inr(inv.total - (inv.paid || 0))} received
          </Button>
        </div>
      )}
      {paid && <p className="text-sm text-primary font-semibold" data-testid="tech-paid-note">Payment received. The invoice is visible to the customer on the Track Booking page.</p>}
      {inv && <SendInvoice job={job} />}
    </div>
  );
};
